import { useEffect, useRef, type ComponentType } from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  FileSignature,
  MessageSquare,
  FolderOpen,
  CalendarDays,
  Mail,
  CircleHelp,
  X,
} from 'lucide-react';
import { useClientAuth } from '../hooks/useClientAuth';

/**
 * Client portal navigation — THE single source of truth for portal destinations.
 *
 * Responsive behaviour is set by `.claude/reference/frontend-ui-system.md`
 * § Responsive breakpoints and § Mobile navigation pattern, both binding:
 * pinned `w-64` sidebar at `lg`, icon-only `w-16` rail between `md` and `lg`,
 * off-canvas drawer with a backdrop below `md`. `phase-05-letter-platform.md` §9.2
 * specifies `ClientShell` as "sidebar with … links; topbar with user name" — this
 * is that sidebar, built for the first time (#18259).
 *
 * ONE nav element serves all three states. Two elements (a rail plus a drawer)
 * would put two navigation landmarks in the accessibility tree at once, which is
 * both an a11y smell and the kind of duplication AC2 forbids.
 */

export interface PortalNavItem {
  /**
   * Destination. MUST start with `/portal`. An absolute path without the prefix
   * escapes the inner router's catch-all and is caught by the OUTER router's
   * `* -> /`, dumping a signed-in client onto the public marketing site — reported
   * from production 2026-09-01 and recorded on HomePage.tsx. Asserted in the test.
   */
  to: string;
  label: string;
  icon: ComponentType<{ className?: string }>;
  /** Which pending count from useClientAuth() to surface, if any. */
  badge?: 'signatures' | 'queries';
}

/**
 * The persistent destinations. THIS ARRAY IS THE ONLY LIST OF PORTAL DESTINATIONS.
 * Adding a route to App.tsx without adding it here (or to the exemption list below)
 * fails ClientPortalNav.test.tsx — which reads App.tsx rather than trusting either.
 */
export const PORTAL_NAV_ITEMS: readonly PortalNavItem[] = [
  { to: '/portal', label: 'Home', icon: LayoutDashboard },
  { to: '/portal/signing', label: 'Signing', icon: FileSignature, badge: 'signatures' },
  { to: '/portal/queries', label: 'Queries', icon: MessageSquare, badge: 'queries' },
  { to: '/portal/documents', label: 'Documents', icon: FolderOpen },
  { to: '/portal/book-appointment', label: 'Book appointment', icon: CalendarDays },
  { to: '/portal/comms-preferences', label: 'Communications', icon: Mail },
  { to: '/portal/help', label: 'Help centre', icon: CircleHelp },
];

/**
 * Portal routes deliberately kept OUT of the nav, each with its reason.
 *
 * Parameterised detail routes (`signing/:id`, `queries/:id`) are NOT listed: they are
 * excluded by construction, being reached from their list pages, and listing them
 * would imply a decision where there is none. Mirrors #16988, which recorded the
 * deliberately-unlinked routes rather than "fixing" them.
 */
export const PORTAL_NAV_EXEMPT_ROUTES: readonly { path: string; reason: string }[] = [
  {
    path: '/portal/onboarding',
    reason:
      'One-time flow, not a destination. Entered from the conditional OnboardingBanner on the '
      + 'portal home (status null -> "Complete your details", Rejected -> "Resubmit") and '
      + 'irrelevant once accepted, so a permanent nav item would be noise for most clients.',
  },
  {
    path: '/portal/*',
    reason: 'Catch-all redirect to /portal. Renders no screen, so there is nothing to navigate to.',
  },
];

interface ClientPortalNavProps {
  /** Drawer open state. Only meaningful below `md`, where the nav is off-canvas. */
  open: boolean;
  /** Escape, backdrop tap, or the close button — the shell restores focus to the hamburger. */
  onClose: () => void;
  /** A nav item was activated: dismiss the drawer without moving focus off the new page. */
  onNavigate: () => void;
}

export function ClientPortalNav({ open, onClose, onNavigate }: ClientPortalNavProps) {
  const { user } = useClientAuth();
  const navRef = useRef<HTMLElement>(null);

  // Focus trap while the drawer is open, plus Escape-to-close (both binding per
  // § Mobile navigation pattern). Focus RESTORATION is the shell's job — it owns
  // the hamburger ref.
  useEffect(() => {
    if (!open) return;
    const node = navRef.current;
    if (!node) return;

    const focusable = () =>
      Array.from(node.querySelectorAll<HTMLElement>('a[href], button:not([disabled])'));

    focusable()[0]?.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const items = focusable();
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey) {
        if (active === first || !node.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else if (active === last || !node.contains(active)) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  const badgeCount = (badge: PortalNavItem['badge']) => {
    if (!user || !badge) return 0;
    return badge === 'signatures' ? user.pendingSignatures : user.pendingQueries;
  };

  return (
    <nav
      ref={navRef}
      id="portal-nav"
      aria-label="Portal navigation"
      className={[
        // Below md: hidden until the hamburger opens it, then a fixed off-canvas panel.
        open ? 'flex fixed inset-y-0 left-0 z-50 w-64 shadow-xl' : 'hidden',
        // md–lg: icon-only rail (w-16) that expands on hover or keyboard focus so the
        // labels are reachable without a pointer (§ No hover-only affordances).
        'md:static md:z-auto md:flex md:w-16 md:shadow-none md:hover:w-64 md:focus-within:w-64',
        // lg and up: pinned full sidebar.
        'lg:w-64',
        'group/nav flex-col shrink-0 overflow-x-hidden gap-2 p-2',
        'bg-[var(--surface)] border-r border-[var(--border)]',
        'transition-[width] duration-200 ease-out motion-reduce:transition-none',
      ].join(' ')}
    >
      {/* Close button — drawer only. The rail and the pinned sidebar are never dismissed. */}
      <button
        type="button"
        onClick={onClose}
        className="md:hidden self-end w-11 h-11 flex items-center justify-center rounded-md text-[var(--text3)] hover:text-[var(--text1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]"
        aria-label="Close navigation"
      >
        <X className="w-5 h-5" />
      </button>

      <ul className="flex flex-col gap-2 list-none m-0 p-0">
        {PORTAL_NAV_ITEMS.map((item) => {
          const Icon = item.icon;
          const count = badgeCount(item.badge);
          return (
            <li key={item.to}>
              <NavLink
                to={item.to}
                // `end` only on the portal root, or it matches every sub-route.
                end={item.to === '/portal'}
                onClick={onNavigate}
                className={({ isActive }) =>
                  // min-h-[44px] + w-11 icon box: >=44x44 effective tap area, and the
                  // gap-2 above gives the >=8px spacing (§ Touch-target sizing, binding).
                  `flex items-center gap-3 min-h-[44px] px-2.5 py-2 rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)] ${
                    isActive
                      ? 'bg-[var(--accent)] text-white'
                      : 'text-[var(--text2)] hover:bg-[var(--surface2)] hover:text-[var(--text1)]'
                  }`
                }
              >
                <span className="w-6 h-6 shrink-0 flex items-center justify-center">
                  <Icon className="w-5 h-5" />
                </span>
                <span
                  className="flex-1 whitespace-nowrap md:opacity-0 md:group-hover/nav:opacity-100 md:group-focus-within/nav:opacity-100 lg:opacity-100 transition-opacity duration-150 motion-reduce:transition-none"
                >
                  {item.label}
                </span>
                {count > 0 && (
                  // The count folds into the link's accessible name ("Signing 3 pending").
                  // NOT aria-label on the span: aria-label on a role-less element is an
                  // axe `aria-prohibited-attr` violation at SERIOUS severity, which AC10 gates.
                  <span className="shrink-0 min-w-[20px] h-5 px-1.5 rounded-full bg-[var(--error)] text-white text-[11px] font-semibold flex items-center justify-center">
                    {count}
                    <span className="sr-only"> pending</span>
                  </span>
                )}
              </NavLink>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
